import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad

Última actualización: 2026

Tu privacidad es importante para nosotros. Esta Política de Privacidad explica cómo recopilamos, usamos y protegemos tu información personal cuando utilizas nuestro sitio web y adquieres nuestros productos digitales.

Información que Recopilamos
Cuando realizas una compra, recopilamos la información necesaria para procesar tu transacción, incluyendo tu nombre, dirección de correo electrónico e información de pago. No almacenamos los datos completos de tu tarjeta de crédito; estos son procesados de forma segura por nuestro proveedor de pagos.

Uso de la Información
Utilizamos tu información personal únicamente para:
• Procesar y entregar tu compra digital.
• Enviarte confirmaciones y actualizaciones relacionadas con tu pedido.
• Mejorar nuestros productos y servicios.

No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.

Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o alteración.

Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.

Tus Derechos
Tienes derecho a acceder, corregir o solicitar la eliminación de tu información personal. Para ejercer estos derechos, puedes contactarnos a través de los canales disponibles en nuestro sitio.

Cambios a Esta Política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Cualquier cambio será publicado en esta página con la fecha de actualización correspondiente.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: 2026

Al acceder a este sitio web y/o adquirir nuestros productos digitales, aceptas cumplir con los siguientes Términos y Condiciones. Si no estás de acuerdo, por favor no utilices nuestro sitio ni realices compras.

Productos Digitales
Nuestros productos son guías digitales de carácter educativo e informativo. El contenido proporcionado no constituye asesoría financiera, legal ni hipotecaria profesional. Te recomendamos consultar con profesionales calificados antes de tomar decisiones financieras.

Entrega del Producto
Una vez completada tu compra, recibirás acceso inmediato al producto digital. Debido a la naturaleza digital del producto, todas las ventas son finales.

Propiedad Intelectual
Todo el contenido, incluyendo textos, gráficos, diseños y materiales descargables, está protegido por derechos de autor. No está permitido reproducir, distribuir ni revender el contenido sin autorización expresa por escrito.

Limitación de Responsabilidad
Los resultados pueden variar según las circunstancias individuales de cada persona. No garantizamos resultados específicos derivados del uso de nuestros productos. La información se proporciona "tal cual" y no nos hacemos responsables de decisiones tomadas basándose en nuestro contenido.

Uso Aceptable
Te comprometes a utilizar nuestro sitio y productos de manera legal y ética. Queda prohibido cualquier uso que infrinja derechos de terceros o viole leyes aplicables.

Modificaciones
Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación en el sitio.

Ley Aplicable
Estos términos se rigen por las leyes aplicables del estado donde opera el negocio.
`.trim();
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} data-id="element-55">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-56" />

      {/* Modal */}
      <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" initial={{
      scale: 0.95,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0.95,
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-57">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-58">
          <h3 className="text-xl font-bold text-[#1B3A5C]" data-id="element-59">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Cerrar" data-id="element-60">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-61" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" data-id="element-62">
          <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line" data-id="element-63">
            {content}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-[#0F2440] py-8 px-4" data-id="element-64">
        <div className="max-w-3xl mx-auto text-center" data-id="element-65">
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-66">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-white/60 hover:text-[#D4A843] transition-colors underline underline-offset-2" data-id="element-67">
              Política de Privacidad
            </button>
            <span className="text-white/30" data-id="element-68">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-white/60 hover:text-[#D4A843] transition-colors underline underline-offset-2" data-id="element-69">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-sm text-white/40" data-id="element-70">
            © 2026 El Plan 0-Down para Inquilinos. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-71">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-72" />}
      </AnimatePresence>
    </>;
}